import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { trackSegment } from '~/components/SegmentAnalytics';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { SavedDashboardsActions } from '~/enums/SavedDashboards';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { slices } from '~/redux/slices';
import { ERROR, SUCCESS } from '~/types/toast.types';

type Props = {
  isLast: boolean;
};

export const DeleteDashboard = ({ isLast }: Props) => {
  const intl = useIntl();
  const activeModal = useSelector(
    slices.savedDashboards.selectors.selectActiveModal,
  );
  const dispatch = useDispatch();
  const user = useCurrentUser();

  const [deleteDashboard, deleteDashboardResult] =
    api.endpoints.deleteDashboard.useMutation();
  const { isLoading } = deleteDashboardResult;

  const handleSuccess = () => {
    showToast(
      SUCCESS,
      intl.formatMessage({
        id: 'DeleteDashboard.successMessage',
        defaultMessage: 'Dashboard deleted successfully.',
        description: 'Success message when dashboard is deleted',
      }),
    );
    dispatch(slices.savedDashboards.actions.closeActiveModal());
  };

  const handleError = (error: any) => {
    const resp = coerceRtkqError(error)?.response || {};
    const msg = resp.data?.errors?.[0];
    showToast(
      ERROR,
      msg?.title ||
        intl.formatMessage({
          id: 'DeleteDashboard.errorMessage',
          defaultMessage: 'An error occurred while deleting the dashboard.',
          description: 'Error message when dashboard deletion fails',
        }),
    );
    dispatch(slices.savedDashboards.actions.closeActiveModal());
  };

  return (
    <Modal
      isVisible={activeModal?.name === SavedDashboardsActions.Delete}
      title={intl.formatMessage({
        id: 'DeleteDashboard.modalTitle',
        defaultMessage: 'Delete Dashboard',
        description: 'Title of the delete dashboard modal',
      })}
      onCancel={() => {
        dispatch(slices.savedDashboards.actions.closeActiveModal());
      }}
      onHide={() => {
        dispatch(slices.savedDashboards.actions.closeActiveModal());
      }}
      primaryAction={{
        text: intl.formatMessage({
          id: 'DeleteDashboard.deleteButton',
          defaultMessage: 'Delete Dashboard',
          description: 'Delete button text',
        }),
        onClick: async () => {
          trackSegment('submitDeleteDashboard', {
            id: activeModal!.selectedDashboard.id,
            name: activeModal!.selectedDashboard.name,
            user_id: user.id,
            client_id: user.client,
          });
          try {
            await deleteDashboard({
              uuid: activeModal!.selectedDashboard.id,
            }).unwrap();
            handleSuccess();
          } catch (error) {
            handleError(error);
          }
        },
        isLoading,
        variant: 'danger',
      }}
    >
      {isLast ? (
        <Text>
          {intl.formatMessage({
            id: 'DeleteDashboard.lastDashboardWarning',
            defaultMessage:
              'Deleting your last Dashboard is final and cannot be undone. Dashboard section will reset back to the two default ones.',
            description: 'Warning message when deleting the last dashboard',
          })}
        </Text>
      ) : (
        <Text>
          <FormattedMessage
            id="DeleteDashboard.deleteConfirmation"
            defaultMessage="Deleting <strong>{dashboardName}</strong> is final and cannot be undone."
            description="Confirmation message for dashboard deletion. Example: dashboardName = 'Sales Dashboard'"
            values={{
              strong: (chunks) => <Text variant="body-bold">{chunks}</Text>,
              dashboardName: activeModal?.selectedDashboard?.name,
            }}
          />
        </Text>
      )}
    </Modal>
  );
};
